import { useMemo } from 'react'

import { useChainId } from 'wagmi'

import { Transaction } from 'state/user/types'
import { useIsSelfGenerated } from 'state/airdrop/hooks'

import { useOpenModal } from 'modal'
import { newTxHistoryModal } from 'modals'

import { getEtherscanLink } from 'utils/crypto'

import { TxCounter } from 'components/features'
import { Button, Icon, Loader } from 'components/ui'

import { TxTypes, AirdropStatus } from 'constants/transaction'

import { Wrapper, Text, WrapContent, StyledLink } from './Style'

type Props = {
  error?: string
  txHash?: string
  txType: TxTypes
  totalTxs?: number
  isPreparing?: boolean
  txHashes?: Transaction[]
  txStatus?: AirdropStatus
  onCurrentClose?: VoidFunction
}

export function AirdropState({
  error,
  txHash,
  txType,
  totalTxs,
  txHashes,
  txStatus,
  isPreparing,
  onCurrentClose,
}: Props) {
  const chainId = useChainId()
  const openModal = useOpenModal()

  const isSelfGenerated = useIsSelfGenerated()

  const _isSend = useMemo(
    () => (txStatus === AirdropStatus.SUCCESS || txStatus === AirdropStatus.FAILED) && !error,
    [txStatus, error],
  )
  const isApproving = useMemo(() => !error && txStatus === AirdropStatus.NOT_STARTED, [error, txStatus])
  const isLoading = useMemo(() => !error && txStatus === AirdropStatus.PENDING, [txStatus, error])

  const topUpType = useMemo(() => txType === TxTypes.TOP_UP_SELF_GENERATED, [txType])
  const airdropType = useMemo(() => txType === TxTypes.AIRDROP || txType === TxTypes.AIRDROP_WITH_SIGNATURE, [txType])

  function openModalTxHistory() {
    onCurrentClose?.()
    openModal(newTxHistoryModal())
  }

  return (
    <WrapContent>
      {isLoading && (
        <Wrapper>
          <Loader size={7.2} />
        </Wrapper>
      )}

      {_isSend && (
        <>
          <Wrapper>
            {txStatus === 'pending' && <Loader size={7.2} />}
            {txStatus === 'success' && <Icon name='success' size='xxl' />}
            {(txStatus === 'failed' || txStatus === 'interrupted') && <Icon name='errorTx' size='xxl' />}
          </Wrapper>
          <Text>
            {txType} {txHashes ? <span>transactions</span> : <span>transaction</span>} has been sent
          </Text>
        </>
      )}

      {isApproving && (
        <>
          <Wrapper>
            <Icon name='codeHands' size='xxl' />
          </Wrapper>
          <Text>
            {isPreparing && <span>Preparing data</span>}

            {isSelfGenerated ? (
              <span>Please wait till your transactions are mint</span>
            ) : (
              <span>Please, sign {txType} transaction in&nbsp;your wallet</span>
            )}
          </Text>
        </>
      )}

      {!error && <TxCounter totalTxs={totalTxs} sentCount={txHashes?.length} withDescription={!isApproving} />}

      {!error && txHash && (
        <StyledLink href={`${getEtherscanLink(chainId, txHash, 'transaction')}`}>
          _explore-tx <Icon name='externalLink' size='extraMedium' />
        </StyledLink>
      )}

      {error && airdropType && (
        <>
          <Wrapper>
            <Icon name='errorTx' size='xxl' />
          </Wrapper>
          <Text type='error'>{error}</Text>
        </>
      )}

      {!error && _isSend && (
        <Button type='outline' onClick={openModalTxHistory}>
          _open-tx-history
        </Button>
      )}

      {error && topUpType && (
        <>
          <Wrapper>
            <Icon name='errorTx' size='xxl' />
          </Wrapper>
          <Text type='error'>{error}</Text>
        </>
      )}
    </WrapContent>
  )
}
